import ScanDetails from '../../api/scans';
import RolesPermissionsMap from '../../services/rolesPermissions';
import Vue from 'vue';
import _ from 'lodash';
import jwt from 'jsonwebtoken';

// Tracks the in-flight merged-selection save per "scan_id:source" (see toggleMergedSelection) so rapid,
// repeated checkbox toggles are persisted one at a time instead of racing each other.
const mergedSelectionSaveChains = {};

// Initial State
const initialState = () => {
  return {
    scanDetails: {},
    userProfile: null,
    isOwner: false,
    uploadFactory: {},
  };
};
const state = initialState();

const getters = {
  isOwner: (state) => {
    return state.isOwner;
  },
  pageLoader: (state) => {
    return state.pageLoader;
  },
  getUserProfile: (state) => {
    return state.userProfile;
  },
  getScanDetails: (state) => {
    return state.scanDetails;
  },
  getUploadFactory: (state) => {
    return state.uploadFactory;
  },
  scanType: (state) => {
    // Determine scan type from query elements
    let scanType = null;
    const queryElements = state.scanDetails && state.scanDetails.query_elements;
    if (queryElements) {
      const { boolean, semantic } = queryElements;
      scanType =
        boolean.length > 0
          ? 'boolean'
          : semantic.length > 0
          ? 'semantic'
          : null;
    }
    return scanType;
  },
  scanCategory: (state) => {
    // Determine scan category from query elements
    let scanCategory = 'topics'; // Assume topics by default
    if (state.scanDetails && state.scanDetails.scan_category) {
      scanCategory = state.scanDetails.scan_category; // Set to scan_category in scan details if present
    }
    return scanCategory;
  },
};

const actions = {
  async setAuthOwner({ commit }, payload) {
    if (payload) {
      let rolesmap = new RolesPermissionsMap();
      let isTruthy = rolesmap.isOwner(payload);
      commit('setIsOwner', isTruthy);
    }
  },
  // This is the action that retrieves the Horizon Scan Data for a particular scan -- This is called on creation of a new scan AND when loading an existing scan
  async retrieveScan({ commit, dispatch, state, rootGetters }, payload) {
    let scanDetails = null;
    let skip_docs = false;
    let newScan = false;
    const scanService = new ScanDetails();

    // When we create a scan, we already push the details to the store so we don't need to do it again
    if (state.scanDetails && Object.keys(state.scanDetails).length > 0) {
      scanDetails = state.scanDetails;
      newScan = true;

      // If this is a newly created scan, we should already have documents -- No need to fetch them again
      if (
        state.scanDetails.documents &&
        state.scanDetails.documents.length > 0
      ) {
        // This is a topics scan
        skip_docs = true;
        // Commit the publication documents to the documents module to enable lazy_loading
        commit(
          'documents/setDocumentsData',
          { source: 'publication', data: state.scanDetails.documents },
          {
            root: true,
          }
        );
        // Commit the patent documents to the documents module to enable lazy_loading
        commit(
          'documents/setDocumentsData',
          { source: 'patent', data: state.scanDetails.patents },
          {
            root: true,
          }
        );
      } else if (
        state.scanDetails.institutions &&
        state.scanDetails.institutions.length > 0
      ) {
        // This is an institutions scan
        skip_docs = true; // Might not need to set this since filters aren't run for institution scans?
        // Commit the institutions data to the documents module to enable lazy_loading
        commit(
          'documents/setDocumentsData',
          { source: 'institution', data: state.scanDetails.institutions },
          { root: true }
        );
        // Set institutions loader to false
        commit(
          'documents/setDocumentLoader',
          {
            source: 'institution',
            data: false,
          },
          { root: true }
        );

        // Commit the funders data to the documents module to enable lazy_loading
        commit(
          'documents/setDocumentsData',
          { source: 'funder', data: state.scanDetails.funders },
          { root: true }
        );
        // Set funders loader to false
        commit(
          'documents/setDocumentLoader',
          {
            source: 'funder',
            data: false,
          },
          { root: true }
        );

        // Commit the patent-entities inventors data to the documents module to enable lazy_loading
        commit(
          'documents/setDocumentsData',
          {
            source: 'inventor',
            data: state.scanDetails.inventors,
          },
          { root: true }
        );
        // Set inventors loader to false
        commit(
          'documents/setDocumentLoader',
          {
            source: 'inventor',
            data: false,
          },
          { root: true }
        );

        // Commit the patent-entities assignees data to the documents module to enable lazy_loading
        commit(
          'documents/setDocumentsData',
          {
            source: 'assignee',
            data: state.scanDetails.assignees,
          },
          { root: true }
        );
        // Set assignee loader to false
        commit(
          'documents/setDocumentLoader',
          {
            source: 'assignee',
            data: false,
          },
          { root: true }
        );
        // bluelens (Others tab) data is not included in scan creation response — fetched lazily below
      } else if (
        state.scanDetails.authors &&
        state.scanDetails.authors.length > 0
      ) {
        // This is a people scan
        skip_docs = true; // Might not need to set this since filters aren't run for people scans?
        // Commit the authors data to the documents module to enable lazy_loading
        commit(
          'documents/setDocumentsData',
          { source: 'author', data: state.scanDetails.authors },
          { root: true }
        );
        // Set authors loader to false
        commit(
          'documents/setDocumentLoader',
          {
            source: 'author',
            data: false,
          },
          { root: true }
        );
        // Commit the patent-entities inventors data to the documents module to enable lazy_loading
        commit(
          'documents/setDocumentsData',
          {
            source: 'inventor',
            data: state.scanDetails.inventors,
          },
          { root: true }
        );
        // Set inventors loader to false
        commit(
          'documents/setDocumentLoader',
          {
            source: 'inventor',
            data: false,
          },
          { root: true }
        );

        // Commit the patent-entities assignees data to the documents module to enable lazy_loading
        commit(
          'documents/setDocumentsData',
          {
            source: 'assignee',
            data: state.scanDetails.assignees,
          },
          { root: true }
        );
        // Set authors loader to false
        commit(
          'documents/setDocumentLoader',
          {
            source: 'author',
            data: false,
          },
          { root: true }
        );
      }

      // Set counts to documents module state
      commit(
        'documents/setNumOfDocs',
        { source: 'publication', data: state.scanDetails.doc_count },
        {
          root: true,
        }
      );
      commit(
        'documents/setNumOfDocs',
        { source: 'patent', data: state.scanDetails.patent_count },
        {
          root: true,
        }
      );
      commit(
        'documents/setNumOfDocs',
        { source: 'institution', data: state.scanDetails.institution_count },
        {
          root: true,
        }
      );
      commit(
        'documents/setNumOfDocs',
        { source: 'funder', data: state.scanDetails.funder_count },
        {
          root: true,
        }
      );
      commit(
        'documents/setNumOfDocs',
        { source: 'author', data: state.scanDetails.author_count },
        {
          root: true,
        }
      );
      commit(
        'documents/setNumOfDocs',
        {
          source: 'inventor',
          data: state.scanDetails.inventor_count,
        },
        {
          root: true,
        }
      );
      commit(
        'documents/setNumOfDocs',
        {
          source: 'assignee',
          data: state.scanDetails.assignee_count,
        },
        {
          root: true,
        }
      );
      commit(
        'documents/setNumOfDocs',
        {
          source: 'bluelens',
          data: state.scanDetails.bpl_entity_count,
        },
        {
          root: true,
        }
      );
    } else {
      // There are no documents yet, that means we are loading a previously existing scan rather than creating a new one
      // Fetch scan details from DB passed on the scan_id in the payload
      const response = await scanService.loadNewScanDetails(payload);
      if (response && response.data && response.success) {
        scanDetails = response.data;
      }
    }
    if (scanDetails) {
      // If user does not have an updated query workflow, then we need to create one
      if (!scanDetails.query && scanDetails.query_elements) {
        const new_data = await scanService.createScan({
          query_elements: scanDetails.query_elements,
          version_id: scanDetails.version_id,
          update_query: true,
        });
        if (new_data.data) scanDetails = new_data.data;
      }
      commit('setDetails', scanDetails);

      // Hydrate merged-view checkbox selections (per data source tab) saved on the scan, so reloading
      // a scan from the dashboard automatically restores which hits were checked.
      if (scanDetails.merged_selections) {
        Object.keys(scanDetails.merged_selections).forEach((source) => {
          commit(
            'documents/setSelectedEntities',
            { source, data: scanDetails.merged_selections[source] },
            { root: true }
          );
        });
      }

      // Get active filter set IDs from scanDetails object
      const activeFilterSetIds = scanDetails.filter_set_ids;

      // Only topics scans use filters and trends
      if (scanDetails.scan_category === 'topics') {
        // Get publication filters from scanDetails object - if active filter set ID is present, use that filter set, otherwise use the first filter set in the filter_sets array
        const publicationFilters =
          activeFilterSetIds && activeFilterSetIds.publication
            ? scanDetails.filter_sets.publication.find(
                (filterSet) =>
                  filterSet.filter_set_id === activeFilterSetIds.publication
              ).filters
            : scanDetails.filter_sets.publication[0].filters;
        // Get patent filters from scanDetails object - if active filter set ID is present, use that filter set, otherwise use the first filter set in the filter_sets array
        const patentFilters =
          activeFilterSetIds && activeFilterSetIds.patent
            ? scanDetails.filter_sets.patent.find(
                (filterSet) =>
                  filterSet.filter_set_id === activeFilterSetIds.patent
              ).filters
            : scanDetails.filter_sets.patent[0].filters;
        // Get funding filters from scanDetails object -- if active filter set ID is present, use that filter set, otherwise use the first filter set in the filter_sets array
        const fundingFilters =
          activeFilterSetIds && activeFilterSetIds.funding
            ? scanDetails.filter_sets.funding.find(
                (filterSet) =>
                  filterSet.filter_set_id === activeFilterSetIds.funding
              ).filters
            : scanDetails.filter_sets.funding[0].filters;
        // Get fundingPublication filters from scanDetails object -- if active filter set ID is present, use that filter set, otherwise use the first filter set in the filter_sets array
        const fundingPublicationFilters =
          activeFilterSetIds && activeFilterSetIds.fundingPublication
            ? scanDetails.filter_sets.fundingPublication.find(
                (filterSet) =>
                  filterSet.filter_set_id ===
                  activeFilterSetIds.fundingPublication
              ).filters
            : scanDetails.filter_sets.fundingPublication[0].filters;
        // Build filters object to be passed to filters Vuex module
        const filters = {
          publication: {},
          patent: {},
          funding: {},
          fundingPublication: {},
        };
        Object.keys(publicationFilters).forEach((key) => {
          filters.publication[key] = publicationFilters[key].values;
        });
        Object.keys(patentFilters).forEach((key) => {
          filters.patent[key] = patentFilters[key].values;
        });
        Object.keys(fundingFilters).forEach((key) => {
          filters.funding[key] = fundingFilters[key].values;
        });
        Object.keys(fundingPublicationFilters).forEach((key) => {
          filters.fundingPublication[key] =
            fundingPublicationFilters[key].values;
        });

        // Dispatch action in filters Vuex module to update global filters for publication index, which will set currentFiltering and dispatch fetchDocuments with the filters
        dispatch(
          'filters/updateGlobalFilters',
          {
            source: 'publication',
            filters: filters.publication,
            skip_docs,
            createScanVersion: false,
            initialLoad: true,
            getFilterHistoryDocCount: scanDetails.filter_set_history_empty
              ? true
              : false, // If filter set history is empty, i.e. only contains the default, then we need to update the doc_count.
          },
          {
            root: true,
          }
        );
        // Dispatch action in filters Vuex module to update global filters for patent index, which will set currentFiltering and dispatch fetchDocuments with the filters
        dispatch(
          'filters/updateGlobalFilters',
          {
            source: 'patent',
            filters: filters.patent,
            skip_docs,
            createScanVersion: false,
            initialLoad: true,
            getFilterHistoryDocCount: scanDetails.filter_set_history_empty
              ? true
              : false, // If filter set history is empty, i.e. only contains the default, then we need to update the doc_count.
          },
          {
            root: true,
          }
        );
        // Dispatch action in filters Vuex module to update global filters for the funding source
        dispatch(
          'filters/updateGlobalFilters',
          {
            source: 'funding',
            filters: filters.funding,
            skip_docs,
            createScanVersion: false,
            initialLoad: true,
            getFilterHistoryDocCount:
              scanDetails.funding_filter_set_history_empty,
          },
          {
            root: true,
          }
        );
        // Dispatch action in filters Vuex module to update global filters for the funding publication source
        dispatch(
          'filters/updateGlobalFilters',
          {
            source: 'fundingPublication',
            filters: filters.fundingPublication,
            skip_docs,
            createScanVersion: false,
            initialLoad: true,
            getFilterHistoryDocCount:
              scanDetails.funding_filter_set_history_empty,
          },
          {
            root: true,
          }
        );

        // Dispatch action in trends Vuex module to fetch trends from publication index
        dispatch(
          'trends/fetchTrends',
          { source: 'publication' },
          { root: true }
        );
        // Dispatch action in trends Vuex module to fetch trends from patent index
        dispatch('trends/fetchTrends', { source: 'patent' }, { root: true });

        // Dispatch action in filters Vuex module to update filter history for publication index
        dispatch(
          'filters/setFilterHistory',
          {
            source: 'publication',
            data: scanDetails.filter_sets.publication,
            activeFilterSetId:
              activeFilterSetIds && activeFilterSetIds.publication
                ? activeFilterSetIds.publication
                : null, // Pass the active filter set id if we have it to set as the active filter set in the filters module
            awaitingFilterHistoryDocCount: scanDetails.filter_set_history_empty
              ? true
              : false, // If filter set history is empty, i.e. only contains the default, then we need to update the doc_count.
          },
          { root: true }
        );
        // Dispatch action in filters Vuex module to update filter history for patent index
        dispatch(
          'filters/setFilterHistory',
          {
            source: 'patent',
            data: scanDetails.filter_sets.patent,
            activeFilterSetId:
              activeFilterSetIds && activeFilterSetIds.patent
                ? activeFilterSetIds.patent
                : null, // Pass the active filter set id if we have it to set as the active filter set in the filters module
            awaitingFilterHistoryDocCount: scanDetails.filter_set_history_empty
              ? true
              : false, // If filter set history is empty, i.e. only contains the default, then we need to update the doc_count.
          },
          { root: true }
        );
        // Dispatch action in filters Vuex module to update filter history for funding source
        dispatch(
          'filters/setFilterHistory',
          {
            source: 'funding',
            data: scanDetails.filter_sets.funding,
            activeFilterSetId:
              activeFilterSetIds && activeFilterSetIds.funding
                ? activeFilterSetIds.funding
                : null, // Pass the active filter set id if we have it to set as the active filter set in the filters module
            awaitingFilterHistoryDocCount:
              scanDetails.funding_filter_set_history_empty ? true : false, // If filter set history is empty, i.e. only contains the default, then we need to update the doc_count
          },
          { root: true }
        );
        // Dispatch action in filters Vuex module to update filter history for funding publication source
        dispatch(
          'filters/setFilterHistory',
          {
            source: 'fundingPublication',
            data: scanDetails.filter_sets.fundingPublication,
            activeFilterSetId:
              activeFilterSetIds && activeFilterSetIds.fundingPublication
                ? activeFilterSetIds.fundingPublication
                : null, // Pass the active filter set id if we have it to set as the active filter set in the filters module
            awaitingFilterHistoryDocCount:
              scanDetails.funding_filter_set_history_empty ? true : false, // If filter set history is empty, i.e. only contains the default, then we need to update the doc_count
          },
          { root: true }
        );

        // Dispatch action to get total funding counts for the scan for the funding source
        dispatch(
          'funding/getFundingCounts',
          {
            source: 'funding',
            type: 'totalFundingCounts',
          },
          { root: true }
        );

        // Dispatch action to get total funding counts for the scan for the funding publication source
        dispatch(
          'funding/getFundingCounts',
          {
            source: 'fundingPublication',
            type: 'totalFundingCounts',
          },
          { root: true }
        );
      } else {
        // Topics scans trigger fetchDocuments via the updateGlobalFilters action -- Since non-topics scans don't use filters, we need to dispatch the fetchDocuments action in the Documents module, passing in skip_docs, for each source -- This handles updating pagination setup in documents module
        // Build payload to pass to fetchDocuments action
        let _documentsPayload = {
          createScanVersion: false,
          activeFilterSetId: null,
          getFilterHistoryDocCount: false,
          skip_docs: skip_docs,
          skip_filters: true, // Non-topics scans don't use filters
        };
        // Define document sources for each scan category
        const documentSources = {
          institutions: [
            'institution',
            'funder',
            'inventor',
            'assignee',
            'bluelens',
            'sanction',
          ],
          people: ['author', 'inventor', 'assignee', 'sanction'],
        };
        // Tabs with document counts list the most documents first (the scan service sorts a new scan's first page the
        // same way); Others and Sanctions list the best matches first
        for (const source of documentSources[state.scanDetails.scan_category]) {
          commit(
            'documents/setPagination',
            {
              source,
              data: {
                ...rootGetters['documents/getPagination'][source],
                sort:
                  source === 'bluelens' || source === 'sanction'
                    ? 'Relevance'
                    : 'DocumentCount',
              },
            },
            { root: true }
          );
        }
        // Iterate over the sources and fetch documents for each
        for (const source of documentSources[state.scanDetails.scan_category]) {
          _documentsPayload.source = source;
          // bluelens and sanction are not included in the scan creation response so always fetch them regardless of skip_docs
          if (source === 'bluelens' || source === 'sanction') _documentsPayload.skip_docs = false;
          else _documentsPayload.skip_docs = skip_docs;
          // Dispatch action in documents module for each source
          await dispatch('documents/fetchDocuments', _documentsPayload, {
            root: true,
          });
        }
      }

      if (scanDetails.query && !newScan) {
        // Build payload to send to scans service to update doc count and growth rate stored in scans DB for this scan version -- do this after kicking off other requests to avoid bottlenecking
        const scanType =
          scanDetails.query_elements &&
          scanDetails.query_elements.boolean.length > 0
            ? 'boolean'
            : 'semantic';

        scanDetails.scan_type = scanType;
        const _payload = {
          scan_id: scanDetails.scan_id,
          scan_version_id: scanDetails.version_id,
          scan_type: scanType,
          user_query: scanDetails.query,
          scan_category: scanDetails.scan_category,
        };
        dispatch('updateScanVersionCounts', _payload);
      }
    }
  },
  // Create a new version number for a scan
  async createScanVersion({ commit, dispatch, state }, payload) {
    let scanService = new ScanDetails();
    const response = await scanService.createScanVersion(payload);
    if (response && response.success && response.data) {
      // Ensure scan details exist in state, ie store not cleared because user has navigated outside of scan before response is received
      if (state.scanDetails && Object.keys(state.scanDetails).length > 0) {
        // Commit new version_id to state
        commit('setScanVersion', response.data.version_id);
        // Dispatch action in filters Vuex module to set filter history with filter sets in response
        dispatch(
          'filters/setFilterHistory',
          {
            source: payload.source,
            data: response.data.filter_sets[payload.source],
            activeFilterSetId: payload.active_filter_set_id || null, // Payload contains active_filter_set_id if user loaded filters from history, pass in dispatch so we can set the active filter set in filters Vuex module
          },
          { root: true }
        );
      }
      // Update the scan in userProfile so new filter data displays in scan cards
      const userProfile = state.userProfile;
      if (userProfile && userProfile.length > 0) {
        // Find the index of the scan in the userProfile array
        const scanIdx = userProfile.findIndex(
          (scan) => scan.scan_id === response.data.scan_id
        );
        // If the scan is found and the scan object is not empty, update the scan in the userProfile array
        if (scanIdx > -1 && Object.keys(userProfile[scanIdx]).length > 0) {
          const payload = {
            ...userProfile[scanIdx],
            active_version_id: response.data.version_id,
            filters: response.data.filter_sets,
          };
          commit('updateUserProfile', { index: scanIdx, data: payload });
        }
      }
    }
  },
  async deleteScan({ dispatch }, payload) {
    let scanService = new ScanDetails();
    const response = await scanService.deleteScan(payload);
    if (response) {
      dispatch('getUserProfile');
      return true;
    } else {
      return false;
    }
  },
  // Dashboard: this will retrieve all the user scans to display
  async getUserProfile({ commit }) {
    let scanService = new ScanDetails();
    let user = sessionStorage.getItem('user');
    user = JSON.parse(user) || null;
    if (user) {
      const response = await scanService.loadUserScans(user.uid);
      if (response && !response.error) {
        response.sort(function (a, b) {
          return new Date(b.created_date) - new Date(a.created_date);
        });
        commit('setUserProfile', response);
        return true;
      } else {
        commit('setUserProfile', []);
        return false;
      }
    }
  },
  async shareScans({ dispatch }, payload) {
    let scanService = new ScanDetails();
    const response = await scanService.shareUserScans(payload);

    dispatch('getUserProfile');

    if (response && response.shared_with) {
      Vue.set(state.scanDetails, 'shared_with', response.shared_with);
      return true;
    } else {
      return false;
    }
  },
  clearStore({ commit }) {
    commit('clearStore');
  },
  async updateScanVersionCounts({}, payload) {
    let scanService = new ScanDetails();
    scanService.updateScanVersionCounts(payload);
  },
  /* Calculate scan metrics for users */
  async fetchUserScanMetrics({ commit, rootGetters }) {
    // we want to get all scans within: last day, last week, last 30 days, last 90 days
    const users = rootGetters['users/getOrgUsers'];
    let user_ids = users.map((user) => user.uid);

    if (!user_ids || !user_ids.length > 0) return;

    let scanService = new ScanDetails();
    const { data } = await scanService.computeUserScanMetrics({ user_ids });

    // add the metrics to the users
    const _users = users.map((user, index) => {
      const user_metrics = data.find((u) => u.user_id === user.uid);

      if (user_metrics) {
        delete user_metrics.user_id;
        user = { ...user, ...user_metrics };
      } else {
        user = {
          ...user,
          ...{
            scans_last_day: 0,
            scans_last_week: 0,
            scans_last_month: 0,
            scans_last_90_days: 0,
            scans_total: 0,
          },
        };
      }
      return user;
    });
    // update the users in the store
    commit('users/setOrgUsers', _users, { root: true });
  },
  // Update scan name
  async updateScanName({ commit }, payload) {
    let scanService = new ScanDetails();
    const response = await scanService.updateScanName(payload);
    if (response && response.success) {
      commit('setScanName', payload.scan_name);
      return true;
    } else {
      return false;
    }
  },
  // Toggle a single hit's "merged view" checkbox for a data source tab (e.g. author, institution) and persist
  // the resulting selection list on the scan so it reloads automatically. Owner-only -- the backend rejects the
  // write for non-owners, and the UI disables the checkboxes for them so this shouldn't normally be reachable.
  async toggleMergedSelection({ commit, rootGetters, state }, { source, entity }) {
    const current = rootGetters['documents/getSelectedIds'][source] || [];
    const exists = current.some((e) => e.entity_id === entity.entity_id);
    const updated = exists
      ? current.filter((e) => e.entity_id !== entity.entity_id)
      : [...current, entity];
    // Update local state immediately so the checkbox/button react without waiting on the network
    commit('documents/setSelectedEntities', { source, data: updated }, { root: true });

    // Chain this save off any save already in flight for this scan+source instead of firing concurrently.
    // Several rapid checkbox clicks each fire their own PATCH; without serializing them, whichever request's
    // DB write lands last silently overwrites the others, and reverting local state on an earlier one's
    // failure would stomp a later click. Each link re-reads the latest selection at send time (not a snapshot
    // from when it was queued), so local state stays the source of truth and self-heals on the next save.
    const scanId = state.scanDetails.scan_id;
    const chainKey = `${scanId}:${source}`;
    const scanService = new ScanDetails();
    const previous = mergedSelectionSaveChains[chainKey] || Promise.resolve();
    const chained = previous.catch(() => {}).then(() => {
      const latest = rootGetters['documents/getSelectedIds'][source] || [];
      return scanService.updateMergedSelections({ scan_id: scanId, source, selections: latest });
    });
    mergedSelectionSaveChains[chainKey] = chained;
    return chained;
  },
  // Retrieve upload factory object from api/scans.js
  buildUploadFactoryObj({ commit }) {
    let scanService = new ScanDetails();
    const response = scanService.providePdfUploadUrlObject();
    if (response) {
      const uploadFactoryObject = {
        ...response,
      };
      commit('setUploadFactoryObj', uploadFactoryObject);
      return true;
    }
    return false;
  },
};

// mutations
const mutations = {
  setIsOwner(state, payload) {
    state.isOwner = payload;
  },
  setUserProfile(state, payload) {
    state.userProfile = payload;
  },
  setDetails(state, payload) {
    state.scanDetails = payload;
  },
  setScanName(state, payload) {
    state.scanDetails.name = payload;
  },
  clearStore(state) {
    state = Object.assign(state, initialState());
  },
  setScanVersion(state, payload) {
    state.scanDetails.version_id = payload;
  },
  setUploadFactoryObj(state, payload) {
    state.uploadFactory = payload;
  },
  updateUserProfile(state, payload) {
    Vue.set(state.userProfile, payload.index, payload.data);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
